import { ILoginResponse } from "stores/auth"
import { newArch2FASetupInstance } from "utils/request"
import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.auth)

export const AUTH_INSTANCE = service.instanceKey

export const AUTH_ENDPOINTS = {
  AUTHENTICATE: "/identity/authenticate",
  USER_SESSION: "/account/getusersession",
  CHECK_SESSION: "/account/checksession",
  FORGOT_PASSWORD: "/account/forgotpassword",
  VIEW_RESET_PW: "/account/view/resetpassword",
  VIEW_SET_PW: "/account/view/setpassword",
  RESET_PASSWORD: "/account/resetpassword",
  SET_PASSWORD: "/account/setpassword",
  VERIFY_2FA: "/account/2fa/verifycode",
  SEND_2FA_CODE: "/account/2fa/sendcode",
  PASSWORD_AUTHENTICATE_2FA: "/identity/2fa/passwordauthenticate",
  SETUP_2FA_INFO: "/account/2fa/setup_info",
  TWO_FA_DETAILS: "/account/2fa/details",
  ENABLE_2FA: "/account/2fa/enable",
  DISABLE_2FA: "/account/2fa/disable",
  VERIFY_ADMIN_PW: "/admin/settings/signin",
  VERIFY_EMPLOYEE_PW: "/employee/settings/signin",
  CHANGE_PASSWORD: "/admin/settings/change_password",
  EMPLOYEE_CHANGE_PASSWORD: "/employee/settings/change_password",
  VIEW_PROFILE: "/admin/settings/view/index",
  EMPLOYEE_VIEW_PROFILE: "/employee/settings/view/index",
  UPDATE_DETAILS: "/admin/settings/update_details",
  EMPLOYEE_UPDATE_DETAILS: "/employee/settings/update_details",
} as const

export const authService = {
  instanceKey: service.instanceKey,

  authenticate: (body: {
    username: string
    password: string
    auth_code?: string
    provider?: string
  }) => service.post(AUTH_ENDPOINTS.AUTHENTICATE, body),

  /** Switch role / app — uses version_number=2 */
  switchApp: (userId: string) =>
    service.post(`${AUTH_ENDPOINTS.AUTHENTICATE}?version_number=2`, {
      user_id: userId,
    }),

  getUserSession: () => service.get(AUTH_ENDPOINTS.USER_SESSION),
  checkSession: () => service.get(AUTH_ENDPOINTS.CHECK_SESSION),

  forgotPassword: (email: string) =>
    service.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, { email }),

  viewResetPassword: (params: Record<string, any>) =>
    service.get(AUTH_ENDPOINTS.VIEW_RESET_PW, params),

  viewSetPassword: (params: { user_id: string; code: string }) =>
    service.get(AUTH_ENDPOINTS.VIEW_SET_PW, params),

  resetPassword: (body: Record<string, any>) =>
    service.post(AUTH_ENDPOINTS.RESET_PASSWORD, body),

  setPassword: (body: Record<string, any>) =>
    service.post(AUTH_ENDPOINTS.SET_PASSWORD, body),

  /** Requires a pre-auth Bearer token (returned from login before 2FA completes) */
  verify2FA: (body: Record<string, any>, token: string) =>
    service.post(AUTH_ENDPOINTS.VERIFY_2FA, body, {
      Authorization: `Bearer ${token}`,
    }),

  send2FACode: (token: string) =>
    service.post(
      AUTH_ENDPOINTS.SEND_2FA_CODE,
      {},
      { Authorization: `Bearer ${token}` }
    ),

  /** Voluntary 2FA setup, step 1. Re-confirms the password; returns a short-lived TwoFactor-context token for the setup calls (not a session token). */
  passwordAuthenticate2FA: (body: { username: string; password: string }) =>
    service.post<ILoginResponse>(
      AUTH_ENDPOINTS.PASSWORD_AUTHENTICATE_2FA,
      body
    ),

  // uses the setup token, not the session token
  setup2FAInfoWithToken: (token: string) =>
    newArch2FASetupInstance
      .get<IBaseRes<{ secret_key: string; barcode_url: string }>>(
        AUTH_ENDPOINTS.SETUP_2FA_INFO,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((r) => r.data),

  verify2FACodeWithToken: (
    body: { generated: boolean; code: string },
    token: string
  ) =>
    newArch2FASetupInstance
      .post<ILoginResponse>(AUTH_ENDPOINTS.VERIFY_2FA, body, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => r.data),

  get2FADetails: () => service.get(AUTH_ENDPOINTS.TWO_FA_DETAILS),
  enable2FA: (body: { code: string }) =>
    service.post(AUTH_ENDPOINTS.ENABLE_2FA, body),
  disable2FA: (body: { code: string }) =>
    service.post(AUTH_ENDPOINTS.DISABLE_2FA, body),
  verifyAdminPassword: (body: { password: string }) =>
    service.post(AUTH_ENDPOINTS.VERIFY_ADMIN_PW, body),
  verifyEmployeePassword: (body: { password: string }) =>
    service.post(AUTH_ENDPOINTS.VERIFY_EMPLOYEE_PW, body),
  changePassword: (body: { oldPassword: string; newPassword: string }) =>
    service.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, body),
  employeeChangePassword: (body: {
    oldPassword: string
    newPassword: string
  }) => service.post(AUTH_ENDPOINTS.EMPLOYEE_CHANGE_PASSWORD, body),
  getProfileDetails: () => service.get(AUTH_ENDPOINTS.VIEW_PROFILE),
  getEmployeeProfileDetails: () =>
    service.get(AUTH_ENDPOINTS.EMPLOYEE_VIEW_PROFILE),
  updateDetails: (body: Record<string, any>) =>
    service.post(AUTH_ENDPOINTS.UPDATE_DETAILS, body),
  employeeUpdateDetails: (body: Record<string, any>) =>
    service.post(AUTH_ENDPOINTS.EMPLOYEE_UPDATE_DETAILS, body),
}
